import { getNostoClient } from './nosto-client.js';
import Logger from './logger.js';

const controllers = new WeakMap();
const triggerSelector = '[data-theme-header-search-toggle], [data-theme-header-search-trigger]';

export function initLazyAutocomplete({ submit, onClient = () => {}, importModule = url => import(url) } = {}) {
	const form = document.querySelector('#search-form');
	const input = form?.querySelector('#search');
	const results = form?.querySelector('#search-results');
	if (!form || !input || !results || window.themeTrackingSuppressed) return;
	if (controllers.has(form)) return controllers.get(form);

	let pending;
	let modulePromise;
	let retries = 0;
	let instance;
	const dialog = form.closest('[data-theme-header-search]');
	const loadModule = () => {
		if (modulePromise) return modulePromise;
		if (!window.themeAutocompleteUrl) return Promise.reject(new Error('Missing autocomplete asset URL'));
		const url = new URL(window.themeAutocompleteUrl, window.location.href);
		// Browsers remember failed module downloads. Keep Shopify's version, but
		// use a fresh module URL when the visitor retries after a network error.
		if (retries) url.searchParams.set('theme_search_retry', retries);
		modulePromise = Promise.resolve().then(() => importModule(url.href)).catch(error => {
			modulePromise = undefined;
			retries += 1;
			throw error;
		});
		return modulePromise;
	};
	const load = () => {
		if (window.themeTrackingSuppressed || !form.isConnected) return Promise.resolve();
		if (instance) return Promise.resolve(instance);
		if (pending) return pending;
		pending = Promise.all([loadModule(), getNostoClient()]).then(([module, client]) => {
			if (window.themeTrackingSuppressed || !form.isConnected) return;
			onClient(client);
			instance = module.initializeAutocomplete({ form, input, results, client, submit });
			removeIntentListeners();
			return instance;
		}).catch(error => {
			Logger.error('Nosto autocomplete unavailable', error);
		}).finally(() => { pending = undefined; });
		return pending;
	};
	const onIntent = event => {
		if (event.type === 'input') {
			if (event.target === input) void load();
		} else if (event.target === input || event.target.closest?.(triggerSelector)) {
			void load();
		}
	};
	const onOpen = () => { void load(); };
	const observer = dialog ? new MutationObserver(() => {
		if (dialog.classList.contains('theme-header-search--active')) void load();
	}) : null;
	const events = ['pointerover', 'focusin', 'click', 'input'];
	const removeIntentListeners = () => {
		events.forEach(type => document.removeEventListener(type, onIntent));
		document.removeEventListener('header.search.open', onOpen);
		observer?.disconnect();
	};
	events.forEach(type => document.addEventListener(type, onIntent));
	document.addEventListener('header.search.open', onOpen);
	observer?.observe(dialog, { attributes: true, attributeFilter: ['class'] });
	const controller = { load };
	controllers.set(form, controller);
	if (document.activeElement === input || dialog?.classList.contains('theme-header-search--active')) void load();
	return controller;
}
